import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, type Persona } from '../lib/auth';
import { ErrorNote, Spinner } from '../ui/components';

const LOGIN_ERRORS: Record<string, string> = {
  no_access:
    'You signed in with One Citizen, but this account has no StaffDesk access yet. Ask HR or your MDA administrator to grant you a role.',
  access_denied: 'One Citizen sign-in was cancelled.',
  idp_unreachable: 'One Citizen is not reachable right now — try again shortly.',
  session_expired: 'The sign-in attempt expired — please try again.',
  state_mismatch: 'The sign-in attempt could not be validated — please try again.',
  token_exchange_failed: 'One Citizen rejected the sign-in — please try again.',
  signin_failed: 'Sign-in failed — please try again.',
};

/** Groups personas by org, preserving PERSONAS' own first-seen order (already grouped by MDA server-side). */
function groupByOrg(personas: Persona[]): Array<[string, Persona[]]> {
  const order: string[] = [];
  const map = new Map<string, Persona[]>();
  for (const p of personas) {
    if (!map.has(p.org)) {
      map.set(p.org, []);
      order.push(p.org);
    }
    map.get(p.org)!.push(p);
  }
  return order.map((org) => [org, map.get(org)!]);
}

export function Login() {
  const { personas, config, login, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loginError = params.get('login_error');
  const loginErrorText = loginError ? (LOGIN_ERRORS[loginError] ?? LOGIN_ERRORS.signin_failed) : null;

  const enter = async (p: Persona) => {
    setBusy(p.id);
    setError(null);
    try {
      await login(p.id);
      navigate('/inbox');
    } catch {
      setError('Sign-in failed — is the gateway running?');
    } finally {
      setBusy(null);
    }
  };

  const groups = groupByOrg(personas);

  return (
    <div className="min-h-screen bg-ink-950">
      <div className="mx-auto max-w-5xl px-6 py-12">
        <header className="mb-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600 text-2xl font-black text-white">
            SC
          </div>
          <h1 className="text-2xl font-black tracking-tight text-white">One Gov StaffDesk</h1>
          <p className="mt-1 text-sm text-slate-400">
            Republic of Seychelles — the government back-office shell: inbox, cases, notifications, consent, documents &amp;
            addresses
          </p>
        </header>

        {loginErrorText ? (
          <div className="mx-auto mb-6 max-w-xl">
            <ErrorNote message={loginErrorText} />
          </div>
        ) : null}
        {error ? (
          <div className="mx-auto mb-6 max-w-xl">
            <ErrorNote message={error} />
          </div>
        ) : null}

        {config.oneCitizen ? (
          <div className="mx-auto mb-10 max-w-md text-center">
            <a
              href="/auth/onecitizen/start"
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-brand-600 px-5 py-3.5 text-base font-bold text-white shadow-lg shadow-brand-600/25 transition hover:bg-brand-700"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15 text-xs font-black">1</span>
              Sign in with One Citizen
            </a>
            <p className="mt-2 text-xs text-slate-500">
              Your national digital identity. StaffDesk roles are granted by HR or your MDA administrator.
            </p>
          </div>
        ) : null}

        {loading ? <Spinner label="Loading sign-in options…" /> : null}

        {config.devLogin && personas.length > 0 ? (
          <p className="mb-4 text-center text-xs font-semibold uppercase tracking-wider text-amber-400/90">
            Development personas — disabled in production (ONEGOV_DEV_LOGIN=0)
          </p>
        ) : null}

        {groups.map(([org, group]) => (
          <section key={org} className="mb-8">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300">{org}</h2>
            <p className="mb-3 mt-0.5 text-xs text-slate-500">{group[0]?.mdaCode}</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {group.map((p) => (
                <button
                  key={p.id}
                  onClick={() => enter(p)}
                  disabled={busy !== null}
                  className="rounded-xl border border-ink-700 bg-ink-900 p-4 text-left transition hover:border-brand-500 hover:bg-ink-800 disabled:opacity-60"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-white">{p.name}</span>
                    {busy === p.id ? (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-500 border-t-white" />
                    ) : null}
                  </div>
                  <div className="mt-0.5 text-xs font-medium text-brand-100">{p.title}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {p.roles.length === 0 ? (
                      <span className="rounded bg-ink-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 ring-1 ring-ink-700">
                        no roles yet
                      </span>
                    ) : (
                      p.roles.map((r) => (
                        <span
                          key={r}
                          className="rounded bg-ink-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 ring-1 ring-ink-700"
                        >
                          {r.replaceAll('_', ' ')}
                        </span>
                      ))
                    )}
                  </div>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
